import * as React from "react";
import { cva } from "class-variance-authority";
import { useNavigate, createSearchParams } from "react-router-dom";
import ArrowDown from "assets/icons/arrowDown.svg";
import { trackingMap } from "utils/tracking";

import { cn } from "lib/utils";

const buttonVariants = cva(
  "select-none flex items-center justify-center whitespace-nowrap rounded-full font-medium ring-offset-white transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-gray-950 dark:focus-visible:ring-gray-300",
  {
    variants: {
      variant: {
        default:
          "bg-blue hover:bg-blue-600 text-white disabled:bg-blue-300 disabled:opacity-100",
        green: "bg-green hover:bg-green-600 text-white disabled:opacity-50",
        white: "bg-white text-black disabled:opacity-50",
        secondary: "bg-white text-black border border-black",
        secondary_no_border: "bg-white text-black",
        tertiary: "bg-white text-black border border-gray-400",
      },
      size: {
        default: "px-[24px] py-regular leading-7",
        small: "px-[12px] py-[4px] leading-7",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Button = React.forwardRef(
  (
    {
      onClick,
      reloadDocument = true,
      className,
      variant,
      size,
      href,
      externalLink = false,
      search,
      queryParams,
      icon: Icon,
      iconPosition = "right",
      children,
      tracking,
      overrideStyles = false,
      ...props
    },
    ref
  ) => {
    const navigate = useNavigate();

    const handleClick = async (e) => {
      if (tracking) {
        tracking.forEach((name) => {
          const resolvedName = trackingMap[name];

          console.log(`Tracked: "${name}" as "${resolvedName}"`);
        });
      }

      if (window.s && tracking) {
        tracking.forEach((name) => {
          const resolvedName = trackingMap[name];

          if (resolvedName) {
            window.s.events = resolvedName;
            window.s.linkTrackEvents = resolvedName;
            window.s.linkTrackVars = "events";
            window.s.tl(null, "o", name);
          }
        });
      }

      if (onClick) {
        const result = await onClick(e);
        if (result === false || e.defaultPrevented) {
          return;
        }
      }
      if (href) {
        if (externalLink) {
          window.open(href, "_blank", "noopener,noreferrer");
          return;
        }
        const navigateOptions = {
          pathname: href,
          replace: reloadDocument ? true : false,
          ...(search ? { search } : {}),
        };
        if (queryParams) {
          navigateOptions.search = createSearchParams(queryParams).toString();
        }
        navigate(navigateOptions);
      }
    };

    const IconComponent = (position) => (
      <>
        {Icon === "arrow_downward" ? (
          <img
            src={ArrowDown}
            alt="Arrow downward icon"
            className={`${position === "left" ? "mr-small -ml-1" : "ml-small -mr-1"} scale-95`}
          />
        ) : (
          <span
            className={cn(
              "material-symbols-outlined align-bottom max-w-[24px]",
              position === "left" ? "-ml-1 mr-small" : "-mr-1 ml-small",
              variant === "tertiary" ? "text-black-300" : "text-secondary",
              size === "small" ? "scale-90" : "scale-[95%]"
            )}
          >
            {Icon}
          </span>
        )}
      </>
    );

    return (
      <button
        className={
          overrideStyles
            ? className
            : cn(buttonVariants({ variant, size, className }))
        }
        ref={ref}
        {...props}
        onClick={handleClick}
      >
        {Icon && iconPosition === "left" && IconComponent("left")}
        <span className="hover:underline">{children}</span>
        {Icon && iconPosition === "right" && IconComponent("right")}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
